import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { Grid, List } from 'react-feather';
import { ReactComponent as AscendingOrder } from '@assets/images/sort_asc.svg';
import { ReactComponent as DescendingOrder } from '@assets/images/sort_desc.svg';
import NotFoundContainer from '@components/common/NotFoundContainer';
import TooltipText from '@components/common/TooltipText';
import { CheckBox, FlexCenterH } from '@components/commonStyle';
import TableFilterDropdown from '@components/table/TableFilterDropdown';
import Pagination from '@components/table/TablePagination';
import { media } from '@styles/mediaQuery';
import { asArray } from '@util/array';
import Format from '@util/formatter';
import styled from 'styled-components';

import useAppStore from '@/store/useAppStore';

const currentPosts = (currentPage: number, postsPerPage: number, tmp: any) => {
  const indexOfLast = currentPage * postsPerPage;
  const indexOfFirst = indexOfLast - postsPerPage;

  let currentPosts = 0;
  currentPosts = tmp.slice(indexOfFirst, indexOfLast);
  return currentPosts;
};

const Container = styled.div<{ $card?: boolean }>`
  display: flex;
  width: 100%;
  overflow-x: auto;
  /* &::-webkit-scrollbar {
    display: none;
  } */

  ${({ $card }) =>
    $card &&
    media.tablet`
      overflow-x: visible;
    `}
`;
const Table = styled.table<{ isStripe?: 'odd' | 'even' | 'none'; $card?: boolean }>`
  width: 100%;
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  color: #555;
  overflow: auto;

  min-height: 28rem;
  /* max-height: 40rem; */

  thead {
    width: 100%;
    display: table;
    overflow: auto;
    font-weight: 600;
    position: sticky;

    top: 0;
    background-color: white;
    border-bottom: 1px solid #eaeaea;
  }
  tbody {
    width: 100%;
    display: table;

    tr {
      &:nth-child(${({ isStripe }) => (isStripe === 'odd' ? 'odd' : 'even')}) {
        /* border-radius: 0.5rem; */
        background-color: ${({ isStripe }) => (isStripe !== 'none' ? '#f5f5f5' : '#ffffff')};
      }
    }
  }

  /* ── 모바일 카드형 자동 전환 (LETMEUP-515) ── */
  ${({ $card }) =>
    $card &&
    media.tablet`
      border: none;
      min-height: auto;
      thead {
        display: none;
      }
      tbody {
        display: block;
      }
      tbody tr {
        display: block;
        height: auto;
        margin-bottom: ${({ theme }) => theme.spacing.md};
        border: 1px solid #eaeaea;
        border-radius: ${({ theme }) => theme.radius.md};
        background-color: #fff !important;
        padding: ${({ theme }) => theme.spacing.sm};
      }
      tbody td {
        display: flex;
        justify-content: space-between;
        align-items: center;
        text-align: right;
        width: auto !important;
        border-bottom: 1px solid #f2f2f2;
        padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xs};
        &:last-child {
          border-bottom: none;
        }
        &::before {
          content: attr(data-label);
          font-weight: 600;
          color: #888;
          text-align: left;
          margin-right: ${({ theme }) => theme.spacing.md};
          flex-shrink: 0;
        }
        /* 카드 내 액션 버튼 모바일 탭타겟 44px */
        button {
          min-height: ${({ theme }) => theme.control.minTapTarget};
        }
      }
      /* 첫 컬럼을 카드 식별 헤더로 강조 */
      tbody td:first-child {
        font-size: ${({ theme }) => theme.typography.h3.size};
        font-weight: 700;
        color: #222;
        border-bottom: 2px solid #eaeaea;
        margin-bottom: ${({ theme }) => theme.spacing.xs};
        padding-bottom: ${({ theme }) => theme.spacing.sm};
        &::before {
          color: #555;
        }
      }
      tbody td > div {
        width: auto !important;
      }
      tbody tr.table-empty-row {
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent !important;
      }
      tbody td.table-empty-cell {
        display: block;
        width: 100% !important;
        padding: 0;
        border: 0;
      }
      tbody td.table-empty-cell::before {
        display: none;
      }
    `}
`;

const Th = styled.th<{ width?: string; isStripe?: 'odd' | 'even' | 'none' }>`
  width: ${(props) => (props.width ? props.width : 'auto')};
  height: 3rem;
  font-weight: 600;
  text-align: center;
  vertical-align: middle;
  padding: 0 0.5rem;
  user-select: text;
  white-space: nowrap;
  border-bottom: ${({ isStripe }) => (isStripe !== 'none' ? 'none' : '1px solid #eaeaea')};
  background-color: white;
`;

const Tr = styled.tr<{ isSelected: boolean; clickable?: boolean }>`
  overflow: auto;
  width: 100%;
  height: 3rem;
  /* background-color: ${({ isSelected }) => (isSelected ? '#1a7cfd14' : '#fff')}; */
  background-color: #f5f5f5;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};

  &:hover {
    /* 행 클릭(clickRowEvent) 가능한 표는 브랜드 연한 틴트로 '클릭 가능'을 강조 */
    background-color: ${({ clickable }) => (clickable ? '#e7e4fc' : '#eaeaea')};
  }
`;

const Td = styled.td<{
  width?: string;
  isStripe?: 'odd' | 'even' | 'none';
}>`
  padding: 0.5rem;

  text-align: center;
  vertical-align: middle;
  border-bottom: ${({ isStripe }) => (isStripe !== 'none' ? 'none' : '1px solid #eaeaea')};

  p {
    margin: 0;
  }
`;

const DivInTd = styled.div<{ width?: string; align?: 'start' | 'center' | 'end' }>`
  width: ${(props) => (props.width ? props.width : 'auto')};
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  display: flex;
  align-items: center;
  justify-content: ${(props) => {
    switch (props.align) {
      case 'start':
        return 'flex-start';
      case 'end':
        return 'flex-end';
      case 'center':
      default:
        return 'center';
    }
  }};
  text-align: ${(props) => props.align || 'center'};
`;

const SortButton = styled.button``;
const TopContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;

  ${media.tablet`
    flex-direction: column;
  `}
`;
const TableInfoContainer = styled.div`
  overflow: hidden;
  color: var(--neutral-gray-50, #555);
  text-overflow: ellipsis;

  /* Table/Total_text */
  font-family: Pretendard;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 500;
  line-height: 100%; /* 1.25rem */

  > span {
    color: var(--primary-primary, #6c60f4);
    font-weight: 700;
  }
`;

// 모바일에서 페이지네이션을 화면 하단에 고정 (LETMEUP-515, LETMEUP-984)
const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  ${media.tablet`
    position: sticky;
    bottom: 0;
    background: #fff;
    padding: ${({ theme }) => theme.spacing.sm} 0;
    box-shadow: 0 -2px 8px rgba(15, 42, 74, 0.06);
    z-index: 5;
  `}
`;

const MobileViewModeToolbar = styled.div`
  display: none;

  ${media.tablet`
    display: flex;
    width: 100%;
    min-height: 2.75rem;
    align-items: center;
    justify-content: space-between;
    padding-top: ${({ theme }) => theme.spacing.xs};
  `}
`;

const MobileViewModeLabel = styled.span`
  color: ${({ theme }) => theme.neutralGray[500]};
  font-size: 0.875rem;
  font-weight: 600;
`;

const MobileViewModeToggle = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  padding: 0.1875rem;
  border: 1px solid ${({ theme }) => theme.main[200]};
  border-radius: 0.625rem;
  background: ${({ theme }) => theme.main[100]};
`;

const MobileViewModeButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  min-width: 3.75rem;
  min-height: 2.25rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.375rem 0.625rem;
  border: 0;
  border-radius: 0.45rem;
  background: ${({ $active, theme }) => ($active ? theme.main[600] : 'transparent')};
  color: ${({ $active }) => ($active ? '#fff' : '#334155')};
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.main[600]};
    outline-offset: 2px;
  }
`;

const EmptyStateContainer = styled(FlexCenterH)<{
  $card?: boolean;
  $minHeight?: React.CSSProperties['minHeight'];
  $maxHeight?: React.CSSProperties['maxHeight'];
  $height?: React.CSSProperties['height'];
}>`
  min-height: ${({ $minHeight }) => $minHeight || '28rem'};
  max-height: ${({ $maxHeight }) => $maxHeight || '40rem'};
  height: ${({ $height }) => $height || '100%'};
  align-items: center;

  ${({ $card }) =>
    $card &&
    media.tablet`
      min-height: 10rem;
    `}
`;

const TableRoot = styled.div`
  display: flex;
  min-width: 0;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  ${media.tablet`
    gap: 0.5rem;
    align-items: stretch;
  `}
`;

export type SortType = 'none' | 'desc' | 'asc';

export type FilterType = {
  key: string;

  filterList: FilterListType[];
  onClickFilterItem?: (key: string, filterValue: FilterListType) => void;
  multiselect?: boolean;
  width?: string;
};

export type FilterListType = {
  id: string;
  value: string;
};

export type ColumnType = {
  accessor: string;
  Header: string | React.ReactNode;
  width?: string;
  type?: TableItem;
  isSort?: boolean;
  sortKey?: string;
  filterKey?: string;
  align?: 'start' | 'center' | 'end';
  Cell?: (props: { value: any; row: any }) => React.ReactNode;
  /**
   * true면 이 컬럼 셀 클릭이 행 클릭(clickRowEvent)으로 전파되지 않음.
   * 토글/버튼 등 자체 동작이 있는 액션 셀을 행 클릭으로부터 보호한다.
   */
  stopRowClick?: boolean;
};

type TableStyleProps = {
  tableContainer?: React.CSSProperties;
  table?: React.CSSProperties;
  thead?: React.CSSProperties;
  tbody?: React.CSSProperties;
  tr?: React.CSSProperties;
  th?: React.CSSProperties;
  td?: React.CSSProperties;
  divInTd?: React.CSSProperties;
};

export type TableProps = {
  columns?: ColumnType[];
  defaultData?: any[];
  data?: any[];
  filters?: FilterType[];
  isCheckbox?: boolean;
  checkboxConfig?: {
    id: string;
    externalChecklist?: any[];
    setExternalChecklist: any;
    checkItem?: (item: any) => void;
    clickRow?: boolean;
  };
  infoConfig?: {
    label1: string;
    label2?: string;
    value1: { type?: 'total' | 'checked'; text?: string };
    value2?: { type?: 'total' | 'checked'; text?: string };
    unit1: string;
    unit2?: string;
  };
  topContainerStyle?: React.CSSProperties;
  style?: TableStyleProps;
  conditionalStyling?: {
    tr: { func: (data: any) => boolean; style: React.CSSProperties };
    td: { key: string; func: (data: any) => boolean; style: React.CSSProperties };
  };
  addOns?: React.ReactNode;
  pagination?: boolean;
  paginationConfig?: {
    postsPerPage?: number;
    currentPage?: number;
    totalcount?: number;
    setCurrentPage?: Dispatch<SetStateAction<number>>;
    fetchData?: (currentPage: number, postsPerPage: number) => void;
  };
  postsPerPage?: number;
  notFoundText?: string;
  clickRowEvent?: (item: any) => void;
  setData?: (data: any[]) => void;
  isStripe?: 'odd' | 'even' | 'none';
  getTableData?: (data: any[]) => void;
  /** ≤768px에서 표를 카드형으로 자동 전환(기본 true). 끄려면 false. (LETMEUP-515) */
  responsiveCard?: boolean;
  /** 모바일 카드/표 전환을 노출하고 마지막 선택을 지점별로 저장한다. */
  allowViewModeSwitch?: boolean;
  /** 같은 경로에 표가 여러 개일 때 저장값을 구분하는 안정적인 키 */
  viewPreferenceKey?: string;
};

const NewTableComponent = ({
  columns,
  defaultData,
  data,
  filters,
  isCheckbox,
  checkboxConfig,
  infoConfig,
  style,
  topContainerStyle,
  conditionalStyling,
  addOns,
  pagination,
  paginationConfig,
  postsPerPage = 10,
  notFoundText,
  clickRowEvent,
  setData,
  isStripe = 'none',
  getTableData,
  responsiveCard = true,
  allowViewModeSwitch = true,
  viewPreferenceKey,
}: TableProps) => {
  const { merchantData } = useAppStore();
  const tableColumns = asArray<ColumnType>(columns);
  const sourceDefaultData = asArray<any>(defaultData);
  const sourceData = asArray<any>(data);
  const tableFilters = asArray<FilterType>(filters);

  const [tableData, setTableData] = useState<any[]>(sourceData);
  const [paginationData, setPaginationData] = useState<any[]>(sourceData);
  const [currentPage, setCurrentPage] = useState(1);
  const [mobileViewMode, setMobileViewMode] = useState<'card' | 'table'>('card');
  const tableRootRef = useRef<HTMLDivElement>(null);
  //const [checklist, setCheckList] = useState<any[]>(checkboxConfig?.externalChecklist || []);
  const [sortType, setSortType] = useState<{ key: string; type: SortType }>({
    key: '',
    type: 'none',
  });

  const [filterState, setFilterState] = useState<{ key: string; value: string | string[] }[]>(
    tableFilters.length
      ? tableFilters.map((filter) => {
          return { key: filter.key, value: 'all' };
        })
      : [],
  );

  const tableIdentity = `${window.location.pathname}:${tableColumns
    .map((column) => column.accessor)
    .join(',')}`;
  const viewModeStorageKey = `letmeup:merchant:${merchantData?.id ?? 'default'}:${
    viewPreferenceKey || `${tableIdentity}:table-view`
  }`;

  useEffect(() => {
    if (!responsiveCard || !allowViewModeSwitch) return;
    const savedViewMode = localStorage.getItem(viewModeStorageKey);
    setMobileViewMode(savedViewMode === 'table' ? 'table' : 'card');
  }, [allowViewModeSwitch, responsiveCard, viewModeStorageKey]);

  const handleMobileViewModeChange = (nextViewMode: 'card' | 'table') => {
    setMobileViewMode(nextViewMode);
    localStorage.setItem(viewModeStorageKey, nextViewMode);
  };

  const useResponsiveCard = responsiveCard && mobileViewMode === 'card';
  const usesExternalPagination = Boolean(paginationConfig);
  useEffect(() => {
    setCurrentPage(1);
    setFilterState(
      tableFilters.length
        ? tableFilters.map((filter) => {
            return { key: filter.key, value: 'all' };
          })
        : [],
    );
  }, [sourceDefaultData.length]);

  // 변경 전
  // useEffect(() => {
  //   if (data) {
  //     setTableData(data);
  //     const posts = currentPosts(currentPage, postsPerPage, data);
  //     setPaginationData(posts as any);
  //   }
  // }, [data]);

  // 변경 후
  useEffect(() => {
    if (sourceData) {
      let next = [...sourceData];

      // 정렬 타입이 none이 아닐 때만 정렬 수행
      if (sortType.type !== 'none' && sortType.key) {
        next = next.sort((valueA, valueB) => {
          if (sortType.type === 'desc') {
            if (valueA[sortType.key] === undefined || valueA[sortType.key] === null) return 1;
            if (valueB[sortType.key] === undefined || valueB[sortType.key] === null) return -1;
            return valueA[sortType.key] < valueB[sortType.key] ? 1 : -1;
          }
          if (valueA[sortType.key] === undefined || valueA[sortType.key] === null) return 1;
          if (valueB[sortType.key] === undefined || valueB[sortType.key] === null) return -1;
          return valueA[sortType.key] > valueB[sortType.key] ? 1 : -1;
        });
      }

      setTableData(next);

      if (!pagination || usesExternalPagination) return setPaginationData(next);
      const posts = currentPosts(currentPage, postsPerPage, next);
      setPaginationData(posts as any);
    }
  }, [data, sortType]);

  useEffect(() => {
    if (pagination && !usesExternalPagination) {
      const posts = currentPosts(currentPage, postsPerPage, tableData);
      checkboxConfig?.setExternalChecklist([]);
      setPaginationData(posts as any);
    }
  }, [currentPage]);

  /**
   * 외부 체크리스트 값 변경
   */
  // useEffect(() => {
  //   checkboxConfig?.setExternalChecklist && checkboxConfig.setExternalChecklist([...checklist]);
  // }, [checklist]);

  useEffect(() => {
    if (!pagination || usesExternalPagination) return setPaginationData(tableData);
    const posts = currentPosts(currentPage, postsPerPage, tableData);
    setPaginationData(posts as any);
    setCurrentPage(1);
  }, [postsPerPage]);

  const filterTableData = (key: string, filterValue: FilterListType | FilterListType[]) => {
    // 컴포넌트 내부의 children이 같은 값인지 판정
    const checkVisible = (targetItem: any, criteria: string) => {
      return targetItem === criteria || targetItem?.props?.children === criteria;
    };
    let filtered = sourceDefaultData;
    const newFilter = filterState.map((filter) => {
      if (filter.key === key) {
        // 하나의 필터에 대해 다중 선택된 경우
        if (Array.isArray(filterValue)) {
          if (filterValue.length === 0) return { key, value: 'all' };
          filtered = filtered.filter((item) =>
            filterValue.some((filterItem) => {
              return checkVisible(item[filter.key], filterItem.value);
            }),
          );
          return { key, value: filterValue.map((item) => item.value) };
        } else {
          // 단일 선택 필터인 경우
          if (filterValue.id === 'all') return { key, value: 'all' };
          filtered = filtered.filter((item) => checkVisible(item[filter.key], filterValue.value));
          return { key, value: filterValue.value };
        }
      } else {
        if (filter.value !== 'all') {
          filtered = filtered.filter((item) =>
            Array.isArray(filter.value)
              ? filter.value.some((filterItem) => item[filter.key] === filterItem)
              : item[filter.key] === filter.value,
          );
        }
        return filter;
      }
    });
    setFilterState(newFilter);
    if (pagination) {
      setPaginationData(
        usesExternalPagination
          ? (filtered as any)
          : (currentPosts(currentPage, postsPerPage, filtered) as any),
      );
    }

    setTableData(filtered);
    getTableData && getTableData(filtered);
    setData && setData(filtered);
  };

  const checkTableItem = (item: any | any[], checkType: 'all' | 'one' = 'one') => {
    if (!isCheckbox || !checkboxConfig) return;
    if (checkType === 'all') {
      if (
        checkboxConfig.externalChecklist &&
        checkboxConfig.externalChecklist.length === paginationData.length
      ) {
        checkboxConfig.setExternalChecklist([]);
      } else {
        checkboxConfig.setExternalChecklist([...paginationData]);
      }
    } else {
      const isExists =
        checkboxConfig.externalChecklist &&
        checkboxConfig.externalChecklist.some(
          (checkItem) => checkItem[checkboxConfig.id] === item[checkboxConfig.id],
        );
      if (isExists) {
        const filtered = checkboxConfig.externalChecklist
          ? checkboxConfig.externalChecklist.filter(
              (checkItem) => checkItem[checkboxConfig.id] !== item[checkboxConfig.id],
            )
          : [];
        checkboxConfig.setExternalChecklist(filtered);
      } else {
        checkboxConfig.setExternalChecklist(
          checkboxConfig.externalChecklist ? [...checkboxConfig.externalChecklist, item] : [item],
        );
      }
    }
  };

  const format = (type: TableItem, value: any) => {
    switch (type) {
      case 'number':
        return Format.formatNumber(value);
      case 'date':
        return Format.formatDate(value);
      case 'datetime':
        return Format.formatDateTime(value);
      case 'point':
        return Format.formatPoint(value);
      case 'percentage':
        return Format.formatPercentage(value);
      case 'phone':
        return Format.formatPhone(value);
      case 'registration':
        return Format.formatRegistrationNumber(value);
      default:
        return value;
    }
  };

  const onClickRow = (item: any) => {
    if (clickRowEvent) clickRowEvent(item);

    if (checkboxConfig && checkboxConfig?.clickRow) checkTableItem(item);
  };

  const sortTableItems = (key: string, sortKey?: string) => {
    try {
      let sortingKey = sortKey ? sortKey : key;
      let changedType: SortType =
        sortType.type === 'asc' ? 'desc' : sortType.type === 'desc' ? 'none' : 'asc';

      if (sortType.key !== sortingKey) {
        changedType = 'asc';
        setSortType({ key: sortingKey, type: 'asc' });
      }
      setSortType({ key: sortingKey, type: changedType });

      if (changedType === 'none') {
        if (tableFilters.length) {
          filterTableData('-1', { id: 'all', value: '-1' });
        } else {
          setTableData(sourceDefaultData);
        }
        return;
      }
      const next = [
        ...tableData.sort((valueA, valueB) => {
          if (changedType === 'desc') {
            if (valueA[sortingKey] === undefined || valueA[sortingKey] === null) return 1;
            if (valueB[sortingKey] === undefined || valueB[sortingKey] === null) return -1;
            return valueA[sortingKey] < valueB[sortingKey] ? 1 : -1;
          }
          if (valueA[sortingKey] === undefined || valueA[sortingKey] === null) return 1;
          if (valueB[sortingKey] === undefined || valueB[sortingKey] === null) return -1;
          return valueA[sortingKey] > valueB[sortingKey] ? 1 : -1;
        }),
      ];
      // if (pagination) setPaginationData([...currentPosts(currentPage, postsPerPage, next)]);
      setTableData(next);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <TableRoot ref={tableRootRef} data-testid="responsive-table-root">
      {(infoConfig || addOns) && (
        <TopContainer style={topContainerStyle}>
          {infoConfig && (
            <TableInfoContainer>
              {infoConfig.label1}{' '}
              <span>
                {infoConfig.value1.type
                  ? infoConfig.value1.type === 'checked'
                    ? checkboxConfig?.externalChecklist?.length
                    : tableData.length
                  : infoConfig.value1.text}
              </span>
              {infoConfig.unit1}
              {infoConfig.unit2 && (
                <>
                  / {infoConfig.label2}{' '}
                  <span>
                    {infoConfig.value2?.type
                      ? infoConfig.value2?.type === 'checked'
                        ? checkboxConfig?.externalChecklist?.length
                        : tableData.length
                      : infoConfig.value2?.text}
                  </span>
                  {infoConfig.unit2}
                </>
              )}
            </TableInfoContainer>
          )}
          {addOns ? addOns : ''}
        </TopContainer>
      )}
      {responsiveCard && allowViewModeSwitch && tableData.length > 0 && (
        <MobileViewModeToolbar>
          <MobileViewModeLabel>보기 방식</MobileViewModeLabel>
          <MobileViewModeToggle aria-label="목록 보기 방식">
            <MobileViewModeButton
              type="button"
              $active={mobileViewMode === 'card'}
              aria-pressed={mobileViewMode === 'card'}
              onClick={() => handleMobileViewModeChange('card')}>
              <Grid size={16} aria-hidden="true" />
              카드
            </MobileViewModeButton>
            <MobileViewModeButton
              type="button"
              $active={mobileViewMode === 'table'}
              aria-pressed={mobileViewMode === 'table'}
              onClick={() => handleMobileViewModeChange('table')}>
              <List size={17} aria-hidden="true" />
              표
            </MobileViewModeButton>
          </MobileViewModeToggle>
        </MobileViewModeToolbar>
      )}
      <Container
        data-testid="responsive-table-scroll"
        style={style?.tableContainer}
        $card={useResponsiveCard}>
        <Table style={style?.table} isStripe={isStripe} $card={useResponsiveCard}>
          <thead style={style?.thead}>
            {isCheckbox && (
              <Th style={{ width: '2.5rem', ...style?.th }} isStripe={isStripe}>
                <CheckBox
                  checked={
                    checkboxConfig?.externalChecklist &&
                    checkboxConfig?.externalChecklist.length === paginationData.length
                  }
                  onChange={() => checkTableItem(sourceData, 'all')}
                />
              </Th>
            )}
            {tableColumns.map((column) => {
              const filtered = tableFilters.length
                ? tableFilters.filter(
                    (filter) =>
                      filter.key === (column.filterKey ? column.filterKey : column.accessor),
                  )
                : [];
              return (
                <Th key={column.accessor} width={column.width ? column.width : 'auto'} style={style?.th}>
                  <div
                    style={{
                      width: column.width ? column.width : '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '5px',
                    }}>
                    {column.isSort && (
                      <SortButton onClick={() => sortTableItems(column.accessor, column.sortKey)}>
                        {sortType.type === 'none' ||
                        sortType.key !== (column.sortKey ? column.sortKey : column.accessor) ? (
                          <AscendingOrder width={20} height={20} fill="#aaa" />
                        ) : sortType.type === 'asc' ? (
                          <AscendingOrder width={20} height={20} fill={'#00BEC1'} />
                        ) : (
                          <DescendingOrder width={20} height={20} fill={'#00BEC1'} />
                        )}
                      </SortButton>
                    )}
                    {tableFilters.length && filtered.length ? (
                      <div>
                        <TableFilterDropdown
                          setCurrentPage={setCurrentPage}
                          defaultData={sourceDefaultData}
                          width={filtered[0].width}
                          titleWidth={column.width}
                          title={column.Header as string}
                          filterList={filtered[0].filterList}
                          onClickDropdownItem={(filterValue: FilterListType | FilterListType[]) => {
                            filterTableData(
                              column.filterKey ? column.filterKey : column.accessor,
                              filterValue,
                            );
                          }}
                          multiselect={filtered[0].multiselect}
                        />
                      </div>
                    ) : (
                      <span>{column.Header}</span>
                    )}
                  </div>
                </Th>
              );
            })}
          </thead>
          <tbody style={style?.tbody}>
            {pagination
              ? paginationData.length > 0 &&
                paginationData.map((item, index) => (
                  <TableRow
                    key={index}
                    rowItem={item}
                    checklist={checkboxConfig?.externalChecklist || []}
                    isCheckbox={isCheckbox}
                    checkboxConfig={checkboxConfig}
                    conditionalStyling={conditionalStyling}
                    style={style}
                    clickRowEvent={clickRowEvent}
                    onClickRow={onClickRow}
                    checkTableItem={checkTableItem}
                    format={format}
                    columns={tableColumns}
                    isStripe={isStripe}
                    index={index}
                  />
                ))
              : tableData.length > 0 &&
                tableData.map((item, index) => (
                  <TableRow
                    key={index}
                    rowItem={item}
                    checklist={checkboxConfig?.externalChecklist || []}
                    isCheckbox={isCheckbox}
                    checkboxConfig={checkboxConfig}
                    conditionalStyling={conditionalStyling}
                    style={style}
                    clickRowEvent={clickRowEvent}
                    onClickRow={onClickRow}
                    checkTableItem={checkTableItem}
                    format={format}
                    columns={tableColumns}
                    isStripe={isStripe}
                    index={index}
                  />
                ))}
            {tableData.length === 0 && (
              <tr className="table-empty-row">
                <td
                  className="table-empty-cell"
                  colSpan={tableColumns.length + (isCheckbox ? 1 : 0)}>
                  <EmptyStateContainer
                    $card={useResponsiveCard}
                    $minHeight={style?.table?.minHeight}
                    $maxHeight={style?.table?.maxHeight}
                    $height={style?.table?.height}>
                    <NotFoundContainer
                      text={notFoundText ? notFoundText : '조회된 내역이 없어요.'}
                    />
                  </EmptyStateContainer>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Container>
      {pagination && tableData.length > 0 && (
        <PaginationWrap>
          {paginationConfig ? (
            <Pagination
              setCurrentPage={
                paginationConfig.setCurrentPage ? paginationConfig.setCurrentPage : setCurrentPage
              }
              currentPage={
                paginationConfig.currentPage ? paginationConfig.currentPage : currentPage
              }
              totalcount={
                paginationConfig.totalcount ? paginationConfig.totalcount : tableData.length
              }
              postsPerPage={
                paginationConfig.postsPerPage ? paginationConfig.postsPerPage : postsPerPage
              }
              fetchData={paginationConfig.fetchData}
            />
          ) : (
            <Pagination
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              totalcount={tableData.length}
              postsPerPage={postsPerPage}
            />
          )}
        </PaginationWrap>
      )}
    </TableRoot>
  );
};

export default NewTableComponent;

type TableRowProps = {
  rowItem: any;
  checklist: any[];
  isCheckbox?: boolean;
  checkboxConfig?: any;
  conditionalStyling?: any;
  style?: TableStyleProps;
  clickRowEvent?: any;
  onClickRow: (item: any) => void;
  checkTableItem: (item: any) => void;
  format: (type: TableItem, value: any) => void;
  columns: ColumnType[];
  isStripe: 'odd' | 'even' | 'none';
  index: number;
};
const TableRow = ({
  columns,
  rowItem,
  isCheckbox,
  checklist,
  checkboxConfig,
  conditionalStyling,
  style,
  clickRowEvent,
  onClickRow,
  checkTableItem,
  format,
  isStripe = 'none',
  index,
}: TableRowProps) => {
  const conditionalRowStyle =
    conditionalStyling && conditionalStyling.tr
      ? conditionalStyling.tr.func(rowItem)
        ? conditionalStyling.tr.style
        : {}
      : {};

  return (
    <Tr
      isSelected={checklist?.some((checkedItem) =>
        checkboxConfig ? checkedItem[checkboxConfig.id] === rowItem[checkboxConfig.id] : false,
      )}
      clickable={!!clickRowEvent}
      style={{ ...conditionalRowStyle, ...style?.tr }}
      onClick={() => onClickRow(rowItem)}>
      {isCheckbox && checkboxConfig && (
        // LETMEUP-525: clickRowEvent와 isCheckbox를 같이 쓰는 화면(예: 회원 목록)에서
        // 체크박스 클릭이 행 클릭(상세 모달 열기 등)까지 같이 트리거되던 문제 방지.
        <Td
          style={{ width: '2.5rem', ...style?.th }}
          isStripe={isStripe}
          onClick={(e) => e.stopPropagation()}>
          <CheckBox
            checked={checklist?.some(
              (checkItem) => checkItem[checkboxConfig.id] === rowItem[checkboxConfig.id],
            )}
            onChange={() => checkTableItem(rowItem)}
          />
        </Td>
      )}
      {columns.map((column) => {
        // 액션 셀(stopRowClick)은 행 클릭을 트리거하지 않으므로 포인터 커서도 주지 않는다.
        let tdStyle: React.CSSProperties = {
          cursor: clickRowEvent && !column.stopRowClick ? 'pointer' : 'default',
          width: column.width,
        };
        if (conditionalStyling && conditionalStyling.td) {
          if (
            conditionalStyling.td.key === column.accessor &&
            conditionalStyling.td.func(rowItem)
          ) {
            tdStyle = {
              ...tdStyle,
              ...conditionalStyling.td.style,
            };
          }
        }
        return (
          <Td
            key={column.accessor}
            style={{ ...tdStyle, ...style?.td }}
            width={column.width}
            isStripe={isStripe}
            data-label={typeof column.Header === 'string' ? column.Header : undefined}
            onClick={column.stopRowClick ? (e) => e.stopPropagation() : undefined}>
            {column.Cell ? (
              <DivInTd width={column.width} align={column.align} style={{ ...style?.divInTd }}>
                {column.Cell({ value: rowItem[column.accessor], row: rowItem })}
              </DivInTd>
            ) : column.type && column.type === 'tooltip' ? (
              <DivInTd
                width={column.width}
                align={column.align}
                style={{ overflow: 'visible', ...style?.divInTd }}>
                <TooltipText width={column.width} text={rowItem[column.accessor]} />
              </DivInTd>
            ) : (
              <DivInTd width={column.width} align={column.align} style={{ ...style?.divInTd }}>
                {column.type
                  ? format(column.type, rowItem[column.accessor])
                  : rowItem[column.accessor] || '-'}
              </DivInTd>
            )}
          </Td>
        );
      })}
    </Tr>
  );
};
